/**
 * tcp-reward-picker.js
 * ─────────────────────────────────────────────────────────────────
 * Free reward eligibility and selection for the cart page.
 *
 * Each reward selection is written as four structured cart
 * attributes per slot so fulfillment scripts can identify
 * the exact source files needed:
 *
 *   Free Print 1 SKU:    TCP-ABSTRACT-001
 *   Free Print 1 Size:   8x10
 *   Free Print 1 Finish: Fine Art Print (Unframed)
 *   Free Print 1 Style:  Horizontal          ← omitted if no style
 *
 *   Free Printable 1 SKU:    TCP-ABSTRACT-001
 *   Free Printable 1 Size:   All Sizes
 *   Free Printable 1 Finish: Digital Files - Printable
 *   Free Printable 1 Style:  Horizontal          ← omitted if no style
 *
 * These appear in Shopify admin under the order's
 * "Additional details" section.
 * ─────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── BOOT ─────────────────────────────────────────────────────────────────────
  function init() {
    const dataEl = document.getElementById('reward-picker-data');
    if (!dataEl) return;

    let parsed;
    try { parsed = JSON.parse(dataEl.textContent); }
    catch (e) { console.error('[reward-picker] Could not parse section data:', e); return; }

    const { config, designs } = parsed;
    if (!designs || designs.length === 0) return;

    // Pre-resolve display image and fulfillment variant for each design.
    designs.forEach(d => {
      const resolved       = resolveVariant(d, config);
      d.resolvedImage      = resolved ? (resolved.image || d.featuredImage) : d.featuredImage;
      d.resolvedVariant    = resolved;
    });

    runUpdate(config, designs);
    listenForCartChanges(config, designs);
  }

  // ── VARIANT RESOLUTION ────────────────────────────────────────────────────────
  /**
   * Finds the best matching variant for fulfillment and image display.
   * Returns the full variant object (including SKU) or null.
   *
   * Priority:
   *   1. Finish + Size + Style (exact match)
   *   2. Finish + Style (any size)
   *   3. Finish only
   */
  function resolveVariant(design, config) {
    if (!design.variants || design.variants.length === 0) return null;

    const optNames  = (design.optionNames || []).map(n => n.toLowerCase());
    const finishIdx = optNames.indexOf(config.finishOptionName.toLowerCase());
    const sizeIdx   = config.sizeOptionName
      ? optNames.indexOf(config.sizeOptionName.toLowerCase()) : -1;
    const styleIdx  = config.styleOptionName
      ? optNames.indexOf(config.styleOptionName.toLowerCase()) : -1;

    function getOpt(v, idx) {
      if (idx < 0) return null;
      return [v.option1, v.option2, v.option3][idx] || null;
    }
    function matchesFinish(v) {
      return getOpt(v, finishIdx) === config.printFinishValue;
    }
    function matchesSize(v) {
      if (sizeIdx < 0) return true;
      const s = getOpt(v, sizeIdx) || '';
      return s.replace(/\s/g, '').toLowerCase() ===
             config.rewardPrintSize.replace(/\s/g, '').toLowerCase();
    }
    function matchesStyle(v) {
      if (!design.styleValue || styleIdx < 0) return true;
      return getOpt(v, styleIdx) === design.styleValue;
    }

    // Priority 1: exact match
    let match = design.variants.find(v =>
      matchesFinish(v) && matchesSize(v) && matchesStyle(v)
    );
    if (match) return match;

    // Priority 2: finish + style, any size
    match = design.variants.find(v => matchesFinish(v) && matchesStyle(v));
    if (match) return match;

    // Priority 3: finish only
    match = design.variants.find(v => matchesFinish(v));
    if (match) return match;

    return null;
  }

  /**
   * Extracts the Size and Style option values from a resolved variant
   * using the product's optionNames array.
   */
  function getVariantOptions(variant, design, config) {
    if (!variant) return { size: null, style: null };

    const optNames = (design.optionNames || []).map(n => n.toLowerCase());
    const sizeIdx  = config.sizeOptionName
      ? optNames.indexOf(config.sizeOptionName.toLowerCase()) : -1;
    const styleIdx = config.styleOptionName
      ? optNames.indexOf(config.styleOptionName.toLowerCase()) : -1;

    function getOpt(v, idx) {
      if (idx < 0) return null;
      return [v.option1, v.option2, v.option3][idx] || null;
    }

    return {
      size:  getOpt(variant, sizeIdx),
      style: getOpt(variant, styleIdx),
    };
  }

  // ── HELPERS ───────────────────────────────────────────────────────────────────
  function designLabel(design) {
    return design.styleValue
      ? `${design.title} — ${design.styleValue}`
      : design.title;
  }

  function designAvailableFor(design, type) {
    const a = design.availability || 'both';
    return a === 'both' || a === type;
  }

  function debounce(fn, ms) {
    let timer;
    return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
  }

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ── CART ──────────────────────────────────────────────────────────────────────
  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function getFinish(item, config) {
    const opt = (item.options_with_values || []).find(o => o.name === config.finishOptionName);
    return opt ? opt.value : null;
  }

  function classifyItem(item, config) {
    const finish = getFinish(item, config);
    if (!finish) return null;
    if (finish === config.printableFinishValue)      return 'printable';
    if (finish === config.printFinishValue)          return 'print';
    if (finish.includes(config.frameFinishContains)) return 'frame';
    return null;
  }

  function calculateEligibility(cart, config) {
    let printableQty = 0, printQty = 0;
    for (const item of cart.items) {
      const type = classifyItem(item, config);
      if (type === 'printable') printableQty += item.quantity;
      if (type === 'print')     printQty     += item.quantity;
    }
    return {
      owedPrintable: Math.floor(printableQty / config.itemsPerReward),
      owedPrint:     Math.floor(printQty     / config.itemsPerReward),
    };
  }

  function getCartProductIds(cart) {
    return new Set(cart.items.map(i => i.product_id));
  }

  // ── SELECTIONS ────────────────────────────────────────────────────────────────
  /**
   * Reads cart attributes and returns a { label → qty } map per type.
   * We key off the SKU attribute to check what's already written.
   * Returns a map of { designLabel → qty } for the stepper UI.
   */
  function getSelectionMap(cart, config, type) {
    const prefix = type === 'printable' ? config.printableAttrPrefix : config.printAttrPrefix;
    const attrs  = cart.attributes || {};
    const map    = {};
    let i = 1;
    // Read the title attribute to rebuild the selection map for UI purposes.
    while (attrs[`${prefix} ${i} Title`] !== undefined) {
      const title = attrs[`${prefix} ${i} Title`];
      const style = attrs[`${prefix} ${i} Style`] || '';
      if (title) {
        const label = style ? `${title} — ${style}` : title;
        map[label] = (map[label] || 0) + 1;
      }
      i++;
    }
    return map;
  }

  /**
   * Writes structured fulfillment attributes for all reward slots.
   *
   * For each slot writes:
   *   {prefix} {n} Title:  e.g. "Abstract No. 1"
   *   {prefix} {n} SKU:    e.g. "TCP-ABSTRACT-001"
   *   {prefix} {n} Size:   e.g. "8x10"
   *   {prefix} {n} Finish: e.g. "Fine Art Print (Unframed)"
   *   {prefix} {n} Style:  e.g. "Horizontal"  (omitted/empty if no style)
   *
   * selectionMap: { designLabel → qty }
   * designs: full designs array (to look up variant data)
   */
  async function writeSelections(config, type, selectionMap, owed, designs) {
    const prefix     = type === 'printable' ? config.printableAttrPrefix : config.printAttrPrefix;
    const finishVal  = type === 'printable' ? config.printableFinishValue : config.printFinishValue;
    const attributes = {};
    let slot = 1;

    for (const [label, qty] of Object.entries(selectionMap)) {
      // Find the matching design for this label.
      const design = designs.find(d => designLabel(d) === label);

      for (let q = 0; q < qty; q++) {
        if (slot > owed) break;

        if (design && design.resolvedVariant) {
          const variant = design.resolvedVariant;
          const opts    = getVariantOptions(variant, design, config);

          attributes[`${prefix} ${slot} Title`]  = design.title;
          attributes[`${prefix} ${slot} SKU`]    = variant.sku || '';
          attributes[`${prefix} ${slot} Size`]   = opts.size || config.rewardPrintSize || '';
          attributes[`${prefix} ${slot} Finish`] = finishVal;
          attributes[`${prefix} ${slot} Style`]  = design.styleValue || '';
        } else {
          // Fallback if variant not resolved — write what we know.
          attributes[`${prefix} ${slot} Title`]  = label;
          attributes[`${prefix} ${slot} SKU`]    = '';
          attributes[`${prefix} ${slot} Size`]   = config.rewardPrintSize || '';
          attributes[`${prefix} ${slot} Finish`] = finishVal;
          attributes[`${prefix} ${slot} Style`]  = '';
        }

        slot++;
      }
    }

    // Clear remaining owed slots and a few beyond.
    for (let s = slot; s <= owed + 5; s++) {
      attributes[`${prefix} ${s} Title`]  = '';
      attributes[`${prefix} ${s} SKU`]    = '';
      attributes[`${prefix} ${s} Size`]   = '';
      attributes[`${prefix} ${s} Finish`] = '';
      attributes[`${prefix} ${s} Style`]  = '';
    }

    await fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ attributes }),
    });
  }

  async function syncAttributeSlots(cart, config, eligibility) {
    const attrs      = cart.attributes || {};
    const attributes = {};

    ['printable', 'print'].forEach(type => {
      const prefix = type === 'printable' ? config.printableAttrPrefix : config.printAttrPrefix;
      const owed   = type === 'printable' ? eligibility.owedPrintable  : eligibility.owedPrint;

      // Find how many slots currently exist by checking Title keys.
      let count = 0;
      while (attrs[`${prefix} ${count + 1} Title`] !== undefined) count++;

      // Preserve existing choices within owed range; clear extras.
      for (let i = 1; i <= Math.max(owed, count); i++) {
        ['Title', 'SKU', 'Size', 'Finish', 'Style'].forEach(field => {
          const key = `${prefix} ${i} ${field}`;
          attributes[key] = i <= owed ? (attrs[key] ?? '') : '';
        });
      }
    });

    await fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ attributes }),
    });
  }

  // ── MAIN UPDATE ───────────────────────────────────────────────────────────────
  async function runUpdate(config, designs) {
    try {
      const cart           = await getCart();
      const eligibility    = calculateEligibility(cart, config);
      const cartProductIds = getCartProductIds(cart);

      // Clear any stale skip attributes left over from the old skip feature.
      await clearSkipAttributes(cart, config);

      await syncAttributeSlots(cart, config, eligibility);
      const freshCart = await getCart();

      const printableMap = getSelectionMap(freshCart, config, 'printable');
      const printMap     = getSelectionMap(freshCart, config, 'print');

      renderUI(config, designs, eligibility, printableMap, printMap, cartProductIds);
    } catch (err) {
      console.error('[reward-picker] Update error:', err);
    }
  }

  /** Removes any Skipped attributes written by the old skip feature. */
  async function clearSkipAttributes(cart, config) {
    const attrs      = cart.attributes || {};
    const printableKey = `${config.printableAttrPrefix} Skipped`;
    const printKey     = `${config.printAttrPrefix} Skipped`;
    if (attrs[printableKey] || attrs[printKey]) {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ attributes: { [printableKey]: '', [printKey]: '' } }),
      });
    }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────────
  function renderUI(config, designs, eligibility, printableMap, printMap, cartProductIds) {
    const wrapper = document.getElementById('reward-picker');
    const slots   = document.getElementById('reward-picker-slots');
    if (!wrapper || !slots) return;

    const totalOwed = eligibility.owedPrintable + eligibility.owedPrint;

    if (totalOwed === 0) {
      wrapper.style.display = 'none';
      slots.innerHTML = '';
      return;
    }

    wrapper.style.display = '';

    // ── Update accordion trigger text ──────────────────────────────────────
    const heading  = wrapper.querySelector('.reward-picker__title');
    const subtitle = wrapper.querySelector('.reward-picker__subtitle');

    const printableOnly = eligibility.owedPrintable > 0 && eligibility.owedPrint === 0;

    if (heading) {
      if (printableOnly) {
        heading.textContent = eligibility.owedPrintable === 1
          ? "You've unlocked a free printable!"
          : `You've unlocked ${eligibility.owedPrintable} free printables!`;
      } else {
        // Prints only or mixed — your existing copy
        heading.textContent = totalOwed === 1
          ? "You've unlocked a free print!"
          : "You've unlocked free prints!";
      }
    }

    if (subtitle) {
      if (printableOnly) {
        subtitle.textContent = eligibility.owedPrintable === 1
          ? "Choose a free printable below — it won't be included unless you select one."
          : "Choose your free printables below — they won't be included unless you select them.";
      } else {
        // Prints only or mixed — your existing copy
        subtitle.textContent = totalOwed === 1
          ? "Choose your favorite from below to include it as a gift in your order."
          : "Choose your favorites from below to include them as a gift in your order.";
      }
    }

    // ── Wire up accordion toggle (once only) ─────────────────────────────
    const trigger = wrapper.querySelector('.reward-picker__trigger');
    if (trigger && !trigger.dataset.bound) {
      trigger.dataset.bound = 'true';
      trigger.addEventListener('click', () => {
        const isOpen = wrapper.classList.toggle('reward-picker--open');
        trigger.setAttribute('aria-expanded', isOpen);
      });
    }

    // ── Build slots ───────────────────────────────────────────────────────
    slots.innerHTML = '';

    if (eligibility.owedPrintable > 0) {
      const available = designs.filter(d =>
        designAvailableFor(d, 'printable') && !cartProductIds.has(d.productId)
      );
      slots.appendChild(
        buildTypeSection(config, 'printable', eligibility.owedPrintable, available, printableMap, designs)
      );
    }

    if (eligibility.owedPrint > 0) {
      const available = designs.filter(d =>
        designAvailableFor(d, 'print') && !cartProductIds.has(d.productId)
      );
      slots.appendChild(
        buildTypeSection(config, 'print', eligibility.owedPrint, available, printMap, designs)
      );
    }
  }

  function buildTypeSection(config, type, owed, designs, selectionMap, allDesigns) {
    const label     = type === 'printable' ? 'Free Printable' : 'Free 8×10 Print';
    const chosen    = Object.values(selectionMap).reduce((a, b) => a + b, 0);
    const remaining = owed - chosen;
    const complete  = remaining <= 0;

    const section = document.createElement('div');
    section.className = 'reward-section';

    section.innerHTML = `
      <div class="reward-section__header">
        <h3 class="reward-section__title">${escapeHTML(label)}${owed > 1 ? 's' : ''}</h3>
        <div class="reward-tally ${complete ? 'reward-tally--complete' : ''}">
          <span class="reward-tally__count">${chosen}</span>
          <span class="reward-tally__sep">/</span>
          <span class="reward-tally__owed">${owed}</span>
          <span class="reward-tally__label"> chosen</span>
        </div>
      </div>
      <p class="reward-section__prompt ${complete ? 'reward-section__prompt--done' : ''}">
        ${complete
          ? '✓ All set! Adjust your choices below if needed.'
          : `Choose ${remaining} more design${remaining !== 1 ? 's' : ''}:`
        }
      </p>
    `;

    const grid = document.createElement('div');
    grid.className = 'reward-grid';

    if (designs.length === 0) {
      grid.innerHTML = `<p class="reward-section__empty">
        All available designs are already in your cart. Contact us for alternatives.
      </p>`;
    } else {
      for (const design of designs) {
        grid.appendChild(buildCard(config, type, design, selectionMap, owed, chosen, allDesigns));
      }
    }

    section.appendChild(grid);

    // ── Terms ─────────────────────────────────────────────────────────────────
    const terms = document.createElement('p');
    terms.className = 'reward-terms';

    const printTerms = `Your free print must be selected now and will be shipped with your order to the same address. Available designs only — no substitutions. Cannot be sent to a separate address or redeemed at a later date.`;
    const printableTerms = `Your free printable must be selected now and will be delivered digitally to the email address on your order. Available designs only — no substitutions. This reward is non-transferable and cannot be redeemed at a later date.`;

    terms.textContent = type === 'printable' ? printableTerms : printTerms;
    section.appendChild(terms);

    return section;
  }

  function buildCard(config, type, design, selectionMap, owed, totalChosen, allDesigns) {
    const label      = designLabel(design);
    const isChecked  = (selectionMap[label] || 0) > 0;
    const isDisabled = !isChecked && totalChosen >= owed;
    const image      = design.resolvedImage;
    const uid        = `reward-cb-${design.productId}-${type}`;

    const card = document.createElement('div');
    card.className = [
      'reward-card',
      isChecked  ? 'reward-card--selected'  : '',
      isDisabled ? 'reward-card--disabled'  : '',
    ].filter(Boolean).join(' ');

    card.innerHTML = `
      ${image
        ? `<div class="reward-card__img-wrap">
             <button class="reward-card__img-btn" type="button"
                     aria-label="View larger image of ${escapeHTML(design.title)}">
               <img class="reward-card__img" src="${escapeHTML(image)}"
                    alt="${escapeHTML(design.title)}" loading="lazy">
               <span class="reward-card__zoom" aria-hidden="true">&#x2922;</span>
             </button>
           </div>`
        : `<div class="reward-card__img-wrap reward-card__img-wrap--placeholder">
             <span aria-hidden="true">🖼</span>
           </div>`
      }
      <p class="reward-card__title">${escapeHTML(design.title)}</p>
      ${design.styleValue
        ? `<p class="reward-card__style">${escapeHTML(design.styleValue)}</p>`
        : ''
      }
      <label class="reward-card__checkbox-label${isDisabled ? ' reward-card__checkbox-label--disabled' : ''}"
             for="${escapeHTML(uid)}">
        <input class="reward-card__checkbox-input"
               type="checkbox"
               id="${escapeHTML(uid)}"
               ${isChecked  ? 'checked'  : ''}
               ${isDisabled ? 'disabled' : ''}
               aria-label="Select ${escapeHTML(design.title)}">
        <span class="reward-card__checkbox-box" aria-hidden="true">
          <svg class="reward-card__checkbox-tick" viewBox="0 0 12 10" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <polyline points="1,5 4.5,8.5 11,1" stroke="currentColor"
                      stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="reward-card__checkbox-text">
          ${isChecked ? 'Selected' : 'Select'}
        </span>
      </label>
    `;

    card.querySelector('.reward-card__img-btn')?.addEventListener('click', () => {
      openLightbox(image, design.title, design.styleValue);
    });

    card.querySelector('.reward-card__checkbox-input').addEventListener('change', async (e) => {
      const newMap = { ...selectionMap };
      if (e.target.checked) {
        newMap[label] = 1;
      } else {
        delete newMap[label];
      }
      e.target.disabled = true;
      await writeSelections(config, type, newMap, owed, allDesigns);
      await runUpdate(config, allDesigns);
    });

    return card;
  }

  // ── LIGHTBOX ──────────────────────────────────────────────────────────────────
  function openLightbox(src, title, styleValue) {
    closeLightbox();
    const largeSrc = src.replace(/_\d+x\d+(\.[a-z]+)$/i, '_1200x1200$1');
    const caption  = styleValue ? `${title} — ${styleValue}` : title;

    const overlay = document.createElement('div');
    overlay.id        = 'reward-lightbox';
    overlay.className = 'reward-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', caption);

    overlay.innerHTML = `
      <div class="reward-lightbox__backdrop"></div>
      <div class="reward-lightbox__panel">
        <button class="reward-lightbox__close" type="button" aria-label="Close">&#x2715;</button>
        <div class="reward-lightbox__img-wrap">
          <img class="reward-lightbox__img" src="${escapeHTML(largeSrc)}"
               alt="${escapeHTML(caption)}" loading="eager">
        </div>
        <p class="reward-lightbox__caption">${escapeHTML(caption)}</p>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => overlay.classList.add('reward-lightbox--open'));

    overlay.querySelector('.reward-lightbox__backdrop').addEventListener('click', closeLightbox);
    overlay.querySelector('.reward-lightbox__close').addEventListener('click', closeLightbox);
    document.addEventListener('keydown', onLightboxKey);
  }

  function closeLightbox() {
    const overlay = document.getElementById('reward-lightbox');
    if (!overlay) return;
    overlay.classList.remove('reward-lightbox--open');
    overlay.addEventListener('transitionend', () => {
      overlay.remove();
      document.body.style.overflow = '';
    }, { once: true });
    document.removeEventListener('keydown', onLightboxKey);
  }

  function onLightboxKey(e) {
    if (e.key === 'Escape') closeLightbox();
  }

  // ── CART CHANGE LISTENERS ─────────────────────────────────────────────────────
  function listenForCartChanges(config, designs) {
    ['cart:updated', 'cart:change', 'cart:refresh', 'theme:cart:updated'].forEach(evt =>
      document.addEventListener(evt, debounce(() => runUpdate(config, designs), 400))
    );

    const cartList = document.querySelector('[data-cart-items], .cart__items, cart-items');
    if (cartList) {
      new MutationObserver(debounce(() => runUpdate(config, designs), 500))
        .observe(cartList, { childList: true, subtree: true });
    }

    setInterval(() => {
      if (!document.hidden) {
        try {
          const d = JSON.parse(document.getElementById('reward-picker-data').textContent);
          d.designs.forEach(des => {
            if (!des.resolvedVariant) {
              const resolved    = resolveVariant(des, d.config);
              des.resolvedImage   = resolved ? (resolved.image || des.featuredImage) : des.featuredImage;
              des.resolvedVariant = resolved;
            }
          });
          runUpdate(d.config, d.designs);
        } catch {}
      }
    }, 4000);
  }

  // ── START ─────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();